import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuotaErrorSurface } from "@/hooks/useQuotaErrorSurface";

export interface SleepReactResult {
  message: string;
  cached: boolean;
}

export interface SleepReactParams {
  sleepLogId: string;
  language: string;
}

export const useSleepReact = () => {
  const [reaction, setReaction] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const surfaceQuota = useQuotaErrorSurface();

  const react = useCallback(async (params: SleepReactParams) => {
    setIsLoading(true);
    setError(null);
    setReaction(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("not_authenticated");
      const { data, error: fnError } = await supabase.functions.invoke("sleep-react", {
        body: {
          sleep_log_id: params.sleepLogId,
          language: params.language,
        },
      });
      if (fnError) {
        if (await surfaceQuota(fnError)) { setIsLoading(false); return; }
        throw fnError;
      }
      const result = data as SleepReactResult;
      if (!result?.message) throw new Error("empty_response");
      setReaction(result.message);
    } catch (err: any) {
      console.error("[useSleepReact] failed", err);
      setError(err?.message || "unknown");
      setReaction(null);
    } finally {
      setIsLoading(false);
    }
  }, [surfaceQuota]);

  const clear = useCallback(() => { setReaction(null); setError(null); }, []);

  return { reaction, isLoading, error, react, clear };
};
